import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Alert,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Chip,
  Switch,
  FormControlLabel
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  Settings,
  Refresh
} from '@mui/icons-material';
import { DataGrid } from '@mui/x-data-grid';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';

const FulfillmentCentersPage = () => {
  const { isAdmin } = useAuth();
  const [centers, setCenters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedCenter, setSelectedCenter] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [centerForm, setCenterForm] = useState({
    name: '',
    code: '',
    api_url: '',
    api_credentials: { type: 'metakocka', api_key: '' },
    is_active: true
  });

  useEffect(() => {
    fetchCenters();
  }, []);

  const fetchCenters = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/fulfillment/centers');
      setCenters(response.data.data || []);
    } catch (error) {
      console.error('Failed to fetch fulfillment centers:', error);
      setError('Failed to load fulfillment centers');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateCenter = () => {
    setSelectedCenter(null);
    setCenterForm({
      name: '',
      code: '',
      api_url: '',
      api_credentials: { type: 'metakocka', api_key: '' },
      is_active: true
    });
    setDialogOpen(true);
  };

  const handleEditCenter = (center) => {
    setSelectedCenter(center);
    setCenterForm({
      name: center.name,
      code: center.code,
      api_url: center.api_url,
      api_credentials: center.api_credentials || { type: 'metakocka', api_key: '' },
      is_active: center.is_active
    });
    setDialogOpen(true);
  };

  const handleSaveCenter = async () => {
    try {
      const centerData = {
        name: centerForm.name,
        code: centerForm.code,
        api_url: centerForm.api_url,
        api_credentials: {
          type: centerForm.api_credentials.type,
          api_key: centerForm.api_credentials.api_key
        },
        is_active: centerForm.is_active
      };

      if (selectedCenter) {
        await axios.put(`/api/fulfillment/centers/${selectedCenter.id}`, centerData);
        setSuccess('Fulfillment center updated successfully');
      } else {
        await axios.post('/api/fulfillment/centers', centerData);
        setSuccess('Fulfillment center created successfully');
      }

      setDialogOpen(false);
      fetchCenters();
    } catch (error) {
      console.error('Failed to save fulfillment center:', error);
      setError('Failed to save fulfillment center: ' + (error.response?.data?.error || error.message));
    }
  };

  const handleDeleteCenter = async (centerId) => {
    if (!window.confirm('Are you sure you want to delete this fulfillment center?')) {
      return;
    }

    try {
      await axios.delete(`/api/fulfillment/centers/${centerId}`);
      setSuccess('Fulfillment center deleted successfully');
      fetchCenters();
    } catch (error) {
      console.error('Failed to delete fulfillment center:', error);
      setError('Failed to delete fulfillment center');
    }
  };

  const handleTestConnection = async (centerId) => {
    try {
      const response = await axios.post(`/api/fulfillment/centers/${centerId}/test-connection`);
      if (response.data.message.includes('successful')) {
        setSuccess('Connection test successful');
      } else {
        setError('Connection test failed: ' + response.data.error);
      }
    } catch (error) {
      console.error('Failed to test connection:', error);
      setError('Connection test failed');
    }
  };

  const columns = [
    {
      field: 'name',
      headerName: 'Name',
      width: 200
    },
    {
      field: 'code',
      headerName: 'Code',
      width: 120
    },
    {
      field: 'api_credentials.type',
      headerName: 'Type',
      width: 150,
      renderCell: (params) => {
        const type = params.row.api_credentials?.type || 'unknown';
        return (
          <Chip
            label={type === 'metakocka' ? 'MetaKocka' : type === 'eliz' ? 'Eli Z' : type}
            color={type === 'metakocka' ? 'primary' : 'secondary'}
            size="small"
          />
        );
      }
    },
    {
      field: 'api_url',
      headerName: 'API URL',
      width: 300
    },
    {
      field: 'is_active',
      headerName: 'Status',
      width: 120,
      renderCell: (params) => (
        <Chip
          label={params.value ? 'Active' : 'Inactive'}
          color={params.value ? 'success' : 'default'}
          size="small"
        />
      )
    },
    {
      field: 'last_sync',
      headerName: 'Last Sync',
      width: 180,
      renderCell: (params) => params.value ? new Date(params.value).toLocaleString() : 'Never'
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 250,
      sortable: false,
      renderCell: (params) => (
        <Box>
          <Button
            size="small"
            startIcon={<Settings />}
            onClick={() => handleTestConnection(params.row.id)}
            sx={{ mr: 1 }}
          >
            Test
          </Button>
          <Button
            size="small"
            startIcon={<Edit />}
            onClick={() => handleEditCenter(params.row)}
            sx={{ mr: 1 }}
          >
            Edit
          </Button>
          <Button
            size="small"
            startIcon={<Delete />}
            onClick={() => handleDeleteCenter(params.row.id)}
            color="error"
          >
            Delete
          </Button>
        </Box>
      )
    }
  ];

  return (
    <Box sx={{ flexGrow: 1 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1" fontWeight="bold">
          Fulfillment Centers
        </Typography>
        <Box>
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={fetchCenters}
            sx={{ mr: 2 }}
          >
            Refresh
          </Button>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={handleCreateCenter}
            disabled={!isAdmin()}
          >
            Add Center
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      {/* Fulfillment Centers Table */}
      <Paper sx={{ height: 600, width: '100%' }}>
        <DataGrid
          rows={centers}
          columns={columns}
          loading={loading}
          disableRowSelectionOnClick
          pagination
          pageSizeOptions={[25, 50]}
          initialState={{
            pagination: {
              paginationModel: { page: 0, pageSize: 25 }
            }
          }}
        />
      </Paper>

      {/* Create/Edit Center Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {selectedCenter ? 'Edit Fulfillment Center' : 'Add New Fulfillment Center'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Name"
                  value={centerForm.name}
                  onChange={(e) => setCenterForm(prev => ({ ...prev, name: e.target.value }))}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Code"
                  value={centerForm.code}
                  onChange={(e) => setCenterForm(prev => ({ ...prev, code: e.target.value }))}
                  required
                  helperText="Unique identifier (e.g., 'metakocka', 'eliz')"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Type</InputLabel>
                  <Select
                    value={centerForm.api_credentials.type}
                    onChange={(e) => setCenterForm(prev => ({ 
                      ...prev, 
                      api_credentials: { ...prev.api_credentials, type: e.target.value }
                    }))}
                    label="Type"
                  >
                    <MenuItem value="metakocka">MetaKocka</MenuItem>
                    <MenuItem value="eliz">Eli Z</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={centerForm.is_active}
                      onChange={(e) => setCenterForm(prev => ({ ...prev, is_active: e.target.checked }))}
                    />
                  }
                  label="Active"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="API URL"
                  value={centerForm.api_url}
                  onChange={(e) => setCenterForm(prev => ({ ...prev, api_url: e.target.value }))}
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="API Key"
                  type="password"
                  value={centerForm.api_credentials.api_key}
                  onChange={(e) => setCenterForm(prev => ({ 
                    ...prev, 
                    api_credentials: { ...prev.api_credentials, api_key: e.target.value }
                  }))}
                  required
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleSaveCenter} variant="contained">
            {selectedCenter ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default FulfillmentCentersPage; 